import clsx from 'clsx';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';
import { useBlocksStore } from '../../hooks/useBlocksStore';
import Button from '../Button';
import InToViewWrapper from '../InToViewWrapper';
import Pagination from '../Pagination'; // Import the Pagination component
import Spinner from '../Spinner';
import Tag from '../Tag';
import BlockSearch from './BlockSearch';

const BlocksGrid = () => {
  const { allBlocks, loading } = useBlocksStore();
  const [query, setQuery] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // State for current page
  const itemsPerPage = 12; // Define how many blocks to show per page

  const filteredBlocks = useMemo(() => {
    return allBlocks.filter((block) => {
      if (rarityFilter !== '' && block.metadata.properties.Rarity !== rarityFilter) return false;
      if (query === '') return true;
      return block.params.name.toLowerCase().includes(query.toLowerCase());
    });
  }, [query, rarityFilter, allBlocks]);

  const totalPages = Math.ceil(filteredBlocks.length / itemsPerPage); // Calculate total pages

  const handlePageChange = (newPage) => {
    // Function to handle page change
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedBlocks = filteredBlocks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage); // Get blocks for the current page

  if (loading) {
    return (
      <div className='mt-20'>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className='mb-6 flex justify-between md:items-end flex-col md:flex-row gap-5 mt-5'>
        <div className='flex flex-col space-y-1'>
          <h1 className='text-2xl font-bold'>Shitty City Blocks</h1>
          <p className='text-sm text-neutral-600'>
            Showing <span className='font-bold'>{filteredBlocks.length}</span> results
          </p>
        </div>
        <div className='flex flex-col md:flex-row gap-4'>
          <Button theme='outline-gray-dark' link href='/marketplace?collection=Shitty City Blocks' className='min-h-[50px]'>
            For Sale
          </Button>
          <BlockSearch onQuery={setQuery} onRarityFilter={setRarityFilter} />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12'>
        {paginatedBlocks.map(
          (
            block,
            index // Use paginated blocks
          ) => (
            <BlocksGridItem key={index} block={block} />
          )
        )}
      </div>

      <Pagination // Use the Pagination component
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export const BlocksGridItem = ({ block, isActive, onClick }) => {
  const imageExtension = useMemo(() => {
    if (block?.index === 1213224198) {
      return 'gif';
    } else {
      return 'png';
    }
  }, [block]);

  if (!block) return <></>;

  if (onClick) {
    return (
      <button
        type='button'
        className={clsx('lg:hover:bg-gray-800 group bg-gray-900 items-center justify-center relative transition duration-300 rounded-lg', {
          'ring-4 ring-primary-500': isActive,
        })}
        onClick={onClick}
      >
        <div className='relative aspect-square w-full'>
          <div className='absolute inset-0 overflow rounded-lg bg-gray-900/75 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300'>
            <p className='text-gray-50 font-medium text-center px-6'>{block.params.name}</p>
          </div>
          <div className='absolute z-20 left-0 bottom-0 p-2'>
            <Tag rarity={block?.metadata?.properties.Rarity} />
          </div>
          <img src={`/blocks/minified/${block.index}.${imageExtension}`} alt={block.params.name} className='rounded-lg' />
          <div className='absolute top-2 right-2 bg-gray-900 rounded-md text-white gap-1  w-10 h-[28px] flex items-end justify-center'>
            <p className='text-gray-400 text-base mb-0.5'>x</p>
            {typeof block.amount === 'number' && <p className='text-right text-xl font-bold mt-1'>{block.amount}</p>}
          </div>
        </div>
      </button>
    );
  }

  return (
    <InToViewWrapper>
      <Link href={`https://www.downbad.farm/asset/${block.index}`} target='_blank'>
        <a className='bg-gray-900 hover:bg-gray-700 transition duration-300 block w-full rounded-lg relative'>
          <div className='relative aspect-square w-full'>
            <div className='absolute z-20 left-0 bottom-0 p-2'>
              <Tag rarity={block?.metadata?.properties.Rarity} />
            </div>
            <img src={`/blocks/minified/${block.index}.${imageExtension}`} alt={block.params.name} className='rounded-t-lg' />
          </div>

          <div className='p-6'>
            <p className='text-lg font-medium'>{block.params.name}</p>
            <p className='text-gray-500 text-base mt-1'>{block.index}</p>
          </div>
        </a>
      </Link>
    </InToViewWrapper>
  );
};

export default BlocksGrid;
